import { useEffect, useMemo, useState } from "react";

export interface RewardItem {
    id: string;
    name: string;
    description: string;
    points: number;
    type: number;
    code?: string;
    image?: string;
}

interface PointsRewardModalProps {
    open: boolean;
    onClose: () => void;
    totalPoints: number;
    rewards: RewardItem[];
    defaultSelected?: string[];
    onApply?: (selectedIds: string[]) => void | Promise<void>;
}

const tabs = [
    { key: 2, label: "Voucher" },
    { key: 1, label: "Vật Phẩm" },
    { key: 3, label: "Thuốc & Y Tế" },
];

export default function PointsRewardModal({
    open,
    onClose,
    totalPoints,
    rewards,
    defaultSelected = [],
    onApply,
}: PointsRewardModalProps) {
    const [activeTab, setActiveTab] = useState<number>(1);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    useEffect(() => {
        if (open) {
            setSelectedIds(defaultSelected);
        }
    }, [open, defaultSelected]);

    const getRewardSelectionKey = (item: RewardItem) => `${item.type}:${item.id}`;

    const filteredRewards = useMemo(() => {
        return rewards.filter((item) => item.type === activeTab);
    }, [activeTab, rewards]);

    const selectedPoints = useMemo(() => {
        return rewards
            .filter((item) => selectedIds.includes(getRewardSelectionKey(item)))
            .reduce((sum, item) => sum + item.points, 0);
    }, [rewards, selectedIds]);

    const remainingPoints = Math.max(0, totalPoints - selectedPoints);

    const toggleSelect = (selectionKey: string) => {
        setSelectedIds((prev) => {
            if (prev.includes(selectionKey)) {
                return prev.filter((itemId) => itemId !== selectionKey);
            }

            const targetReward = rewards.find((item) => getRewardSelectionKey(item) === selectionKey);
            if (!targetReward) {
                return prev;
            }

            const currentSelectedPoints = rewards
                .filter((item) => prev.includes(getRewardSelectionKey(item)))
                .reduce((sum, item) => sum + item.points, 0);

            if (currentSelectedPoints + targetReward.points > totalPoints) {
                return prev;
            }

            return [...prev, selectionKey];
        });
    };

    const handleApply = async () => {
        await onApply?.(selectedIds);
        onClose();
    };

    if (!open) return null;

    return (
        <div role="presentation" className="MuiModal-root styles_modalContainer__Sdvdc css-8ndowl" style={{ zIndex: 1400 }}>
            <div className="MuiBackdrop-root" style={{ opacity: 1 }} onClick={onClose} />

            <div
                className="MuiGrid-root styles_confirm_modal_wrap__q6yk7"
                style={{ maxWidth: 680, width: "calc(100vw - 32px)", maxHeight: "calc(100vh - 32px)" }}
            >
                <div className="MuiGrid-root styles_modal_title__Tpult MuiGrid-container MuiGrid-align-items-xs-center MuiGrid-justify-content-xs-space-between">
                    <p className="MuiTypography-root styles_modalVoucherList_title__m5Bv3 MuiTypography-body1">
                        Đổi Điểm Nhận Ưu Đãi
                    </p>
                    <svg className="MuiSvgIcon-root styles_close__JfKfm" viewBox="0 0 24 24" onClick={onClose}>
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 24px", borderBottom: "1px solid #e5e7eb" }}>
                    <p className="MuiTypography-root MuiTypography-body2">Điểm hiện tại: <strong>{totalPoints.toLocaleString("vi-VN")}</strong></p>
                    <p className="MuiTypography-root MuiTypography-body2">Còn lại: <strong style={{ color: "#009f7f" }}>{remainingPoints.toLocaleString("vi-VN")}</strong></p>
                </div>

                <div style={{ display: "flex", gap: 12, padding: "0 24px", borderBottom: "1px solid #e5e7eb" }}>
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                border: "none",
                                background: "transparent",
                                padding: "16px 0 12px",
                                fontWeight: 600,
                                color: activeTab === tab.key ? "#009f7f" : "#6b7280",
                                borderBottom: activeTab === tab.key ? "2px solid #009f7f" : "2px solid transparent",
                                cursor: "pointer",
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div style={{ overflowY: "auto", padding: 16, maxHeight: 360, background: "#f9fafb" }}>
                    {filteredRewards.map((item) => {
                        const selectionKey = getRewardSelectionKey(item);
                        const isSelected = selectedIds.includes(selectionKey);
                        const isDisabled = !isSelected && item.points > remainingPoints;

                        return (
                            <button
                                key={item.id}
                                type="button"
                                disabled={isDisabled}
                                onClick={() => toggleSelect(selectionKey)}
                                style={{
                                    width: "100%",
                                    textAlign: "left",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 12,
                                    padding: 14,
                                    marginBottom: 12,
                                    background: "#fff",
                                    cursor: isDisabled ? "not-allowed" : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    opacity: isDisabled ? 0.55 : 1,
                                }}
                            >
                                <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                                    <div style={{ width: 64, height: 64, borderRadius: 10, border: "1px solid #f3f4f6", display: "grid", placeItems: "center" }}>
                                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M20.59 13.41L11.58 22.41C11.22 22.78 10.72 23 10.17 23C9.62 23 9.11 22.78 8.76 22.41L1.59 15.24C1.22 14.89 1 14.38 1 13.83V3C1 1.9 1.9 1 3 1H13.83C14.38 1 14.89 1.22 15.24 1.59L22.41 8.76C22.78 9.11 23 9.62 23 10.17C23 10.72 22.78 11.22 22.41 11.58L20.59 13.41Z" stroke="#f97316" strokeWidth="1.5" />
                                            <circle cx="7" cy="7" r="1.5" fill="#f97316" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="MuiTypography-root MuiTypography-body1" style={{ margin: 0, fontWeight: 700 }}>{item.name}</p>
                                        <p className="MuiTypography-root MuiTypography-body2" style={{ margin: "4px 0", color: "#6b7280" }}>{item.description}</p>
                                        <p className="MuiTypography-root MuiTypography-body2" style={{ margin: 0, color: "#f97316", fontWeight: 600 }}>★ {item.points.toLocaleString("vi-VN")} điểm</p>
                                        {isDisabled && (
                                            <p className="MuiTypography-root MuiTypography-body2" style={{ margin: "4px 0 0", color: "#d14343", fontWeight: 600 }}>
                                                Không đủ điểm để chọn ưu đãi này.
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <span
                                    style={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "999px",
                                        border: `2px solid ${isSelected ? "#009f7f" : "#c7cdd4"}`,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: isSelected ? "#009f7f" : "transparent",
                                    }}
                                >
                                    {isSelected && <span style={{ width: 10, height: 10, borderRadius: 999, background: "#fff" }} />}
                                </span>
                            </button>
                        );
                    })}

                    {filteredRewards.length === 0 && (
                        <p className="MuiTypography-root MuiTypography-body2" style={{ textAlign: "center", color: "#6b7280", marginTop: 20 }}>
                            Chưa có ưu đãi trong danh mục này.
                        </p>
                    )}
                </div>

                <div style={{ borderTop: "1px solid #e5e7eb", padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: 16 }}>
                    <button type="button" onClick={onClose} className="MuiButtonBase-root MuiButton-root MuiButton-text" style={{ color: "#4b5563", fontWeight: 600 }}>
                        Hủy bỏ
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        className="styles_button__l7oVR styles_base__Pdm9S styles_contained__4kDf4 styles_colorPrimary__NGf0C"
                        style={{ minWidth: 180 }}
                    >
                        Áp dụng ({selectedIds.length} mục)
                    </button>
                </div>
            </div>
        </div>
    );
}